import React from 'react';
import { useAppStateContext } from '../../../AppStateProvider/AppStateProvider';
import { Button, Container, Grid, Typography, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import ErrorIllustration from './ErrorIllustration.png';
import { ErrorIcon } from '../../../../icons/ErrorIcon';

const MainContainer = styled('div')({
  display: 'block',
});

const Heading = styled(Typography)({
  position: 'relative',
  marginTop: '20px',
});

const GrantPermissions = styled(Typography)({
  fontSize: '16px',
  paddingBottom: '16px',
});

const Illustration = styled('img')(({ theme }) => ({
  maxHeight: '174px',
  maxWidth: '337px',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const Header = styled(Grid)(({ theme }) => ({
  float: 'left',
  [theme.breakpoints.down('md')]: {
    float: 'initial',
  },
}));

const RefreshButton = styled(Grid)(({ theme }) => ({
  clear: 'left',
  [theme.breakpoints.down('md')]: {
    clear: 'initial',
    marginBottom: '2em',
  },
}));

const PaperContainer = styled(Grid)(({ theme }) => ({
  float: 'right',
  marginRight: '1em',
  [theme.breakpoints.down('md')]: {
    float: 'initial',
    display: 'flex',
    justifyContent: 'center',
    margin: '0 0 2.5em 0',
  },
}));

const StyledPaper = styled(Paper)({
  display: 'inline-block',
  padding: '20px',
  borderRadius: '8px',
});

export function getDeviceErrorPaneContent(error?: Error) {
  let headline = '';
  let message = '';

  switch (true) {
    // This error is emitted when the user or the user's system has denied permission to use the media devices
    case error?.name === 'NotAllowedError':
      headline = 'Permissions needed';

      if (error!.message === 'Permission denied by system') {
        // Chrome only
        message =
          'The operating system has blocked the browser from accessing the microphone or camera. Please check your operating system settings.';
      } else {
        message = `We can't access your microphone/camera which means we don't have permissions for audio and video. Depending on your browser or operating system, these might live in "Settings".`;
      }

      break;

    // This error is emitted when input devices are not connected or disabled in the OS settings
    case error?.name === 'NotFoundError':
      headline = 'Cannot find microphone or camera';
      message =
        'The browser cannot access the microphone or camera. Please make sure all input devices are connected and enabled.';
      break;

    // Other getUserMedia errors are less likely to happen in this app. Here we will display
    // the system's error message directly to the user.
    case Boolean(error):
      headline = 'Error acquiring media';
      message = `${error!.name} ${error!.message}`;
      break;
  }
  return {
    headline,
    message,
  };
}

export function PermissionError() {
  const { state } = useAppStateContext();
  const { headline, message } = getDeviceErrorPaneContent(state.deviceError!);

  return (
    <Container>
      <MainContainer>
        <Header item lg={5}>
          <Heading variant="h1" gutterBottom>
            <ErrorIcon />
            {headline}
          </Heading>

          <Typography variant="body1" gutterBottom>
            {message}
          </Typography>
        </Header>

        <PaperContainer item lg={5}>
          <StyledPaper>
            <GrantPermissions variant="body1">
              <strong>Permissions</strong>
            </GrantPermissions>
            <Illustration src={ErrorIllustration} alt="Settings Illustration" />
          </StyledPaper>
        </PaperContainer>

        <RefreshButton item lg={5}>
          <Typography variant="body1" gutterBottom>
            <strong>Update your settings to allow your browser permissions and refresh this page.</strong>
          </Typography>

          <Button variant="contained" color="primary" onClick={() => window.location.reload()}>
            Refresh page
          </Button>
        </RefreshButton>
      </MainContainer>
    </Container>
  );
}
